/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Drew Burnett
 */

import React, { useState } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { useSelector } from "react-redux"
import {
    Modal,
    Column,
    useIsLandscape,
    ErrorRotate,
    TitleSection,
    Label,
    useToken,
    updateMomentWithEditorTags,
} from "trux"

import { PersonalizeMoment } from "trux/dist/organisms/PersonalizeMoment"
import { useFullVideoByMomentQuery } from "../../generated/graphql"
import "./WrappedEditMomentPage.css"
import { useSuspendedVideo } from "../../hooks/useSuspendedVideo"

// Edit Moment

export const WrappedEditMomentPage = (props) => {
    const history = useHistory()
    const location = useLocation()
    const isLandscapeMode = useIsLandscape()

    // Suspend the background video
    useSuspendedVideo(props.videoContainerRef)

    // loading
    const [loading, setLoading] = useState(false)

    // Token
    const token = useToken()
    const tokenReady = token !== null

    // General
    const team = useSelector((state) => state.team.value)
    const game = useSelector((state) => state.game.value)
    const user = useSelector((state) => state.user.value)
    const momentEventNumber = props.match.params.event_number
    let isCoach = false
    if (user && team && team.members) {
        for (let member of team.members) {
            if (member.user_id === user.user_id) {
                // eslint-disable-next-line eqeqeq
                isCoach = member.is_coach == "1"
                break
            }
        }
    }

    //// Start and End Times
    let startVal = 0
    let pathParts = props.location.pathname.split("/")
    for (let part of pathParts) {
        if (part.includes("start=")) {
            startVal = parseInt(part.replace("start=", ""))
            if (startVal !== startVal) {
                startVal = 0
            }
        }
    }

    // On close handler
    const handleOnClose = () => {
        // Remove the pure black class to the html element
        const element = document.querySelector("html")
        element.classList.remove("bg-pure-black-color-important")
        history.replace(
            history.replace(
                props.location.pathname
                    .replace("edit-moment", "")
                    .replace("/options", "")
                    .replace(/\/start.*$/gi, "")
            )
        )
    }

    // Get the moment
    let moment = useSelector((state) => {
        let m = null
        const momentUrlParts = momentEventNumber.split(":")
        const eventCamera = momentUrlParts && momentUrlParts.length > 1 ? momentUrlParts[1] : "tracecam"
        if (state.game.value && state.game.value.events) {
            for (let event of state.game.value.events) {
                if (event.event_number === parseInt(momentEventNumber)) {
                    if (eventCamera !== "tracecam" && event.sources && event.sources.length > 0) {
                       for (let source of event.sources) {
                        if (source.tags.find(tag => tag.name === eventCamera)) {
                            m = Object.assign({}, event, source)
                        }
                       }
                    } else {
                        m = Object.assign({}, event)
                        break
                    }
                }
            }
        }
        if (m && isCoach) {
            let ca = m.tags.find((t) => t.name === "coach-audio")
            if (!ca) {
                m.tags.push({ name: "coach-audio" })
            }
        }
        return m
    })

    // Get the full video moment
    const { data: fullVideoData } = useFullVideoByMomentQuery({
        variables: {
            game_clip_id: moment?.game_clip_id,
            token: token,
        },
        skip: !tokenReady || !moment,
    })
    const fullVideoMoment = fullVideoData
        ? fullVideoData.fullVideoByMoment
        : null

    // If we don't have the moment, return
    if (!moment || !fullVideoMoment) {
        return null
    }

    // Our on save handlers
    const handleOnSave = async (updates) => {
        let updatedMoment = updateMomentWithEditorTags(moment, updates.tags)

        // Construct the body params
        let url = `https://lapi.traceup.com/tracebot-${process.env.REACT_APP_TRACE_ENV}/clips/edit-meta`
        let params = {
            game_clip_id: moment.game_clip_id,
            type: updatedMoment.meta.type,
            start_location: updatedMoment.meta.thirds[0],
            end_location: updatedMoment.meta.thirds[1],
            trace_numbers: updatedMoment.meta.trace_numbers,
            csp: updates.csp,
            editor_tags: updatedMoment.tags.map((t) => t.name),
            user_id: user.user_id,
            token: token,
        }

        // Make the call to create the moment
        setLoading(true)
        fetch(url, {
            method: "POST",
            body: JSON.stringify(params),
            mode: "cors",
        })
            .then((res) => {
                return res.json()
            })
            .then((json) => {
                if (json.error) {
                    alert(json.error)
                    setLoading(false)
                } else {
                    setLoading(false)
                    let path = location.pathname.replace("/edit-moment/", "/")
                    path = path.replace("/options", "")
                    history.replace(path)
                    window.location.reload()
                }
            })
    }

    // Massage our moment for the PersonalizeMoment dialog
    let fullGameMoment = {
        base_path: fullVideoMoment.base_path,
        dynamic_audio: fullVideoMoment.dynamic_audio,
        dynamic_duration: fullVideoMoment.dynamic_duration,
        dynamic_hls: fullVideoMoment.dynamic_hls,
        dynamic_jfile: fullVideoMoment.dynamic_jfile,
        dynamic_offset: fullVideoMoment.dynamic_offset,
        dynamic_start: fullVideoMoment.dynamic_start,
        half: fullVideoMoment.half,
        game_clip_id: fullVideoMoment.game_clip_id,
        game_id: fullVideoMoment.game_id,
        team_id: fullVideoMoment.team_id,
        start_time: fullVideoMoment.start_time,
        tags: [],
        time: fullVideoMoment.time,
        meta: {
            type: fullVideoMoment.type,
            thirds: fullVideoMoment.thirds ? fullVideoMoment.thirds : [],
            keywords: fullVideoMoment.keywords ? fullVideoMoment.keywords : [],
            trace_numbers: fullVideoMoment.trace_numbers,
            camera: { ...moment.camera },
        },
    }

    const momentUTCTime = fullVideoMoment.start_time
    const momentFullGameTime = fullVideoMoment.time
    const massagedMomentStartTime =
        momentUTCTime + (startVal * 1000 - momentFullGameTime * 1000 - 2000)
    let massagedMoment = {
        ...fullGameMoment,
        time: fullVideoMoment.time,
        start_time: massagedMomentStartTime,
        duration: moment.duration,
        tags: moment?.tags,
        dynamic_duration: moment.duration, // start with a 10s moment
        sport_type: moment.sport_type,
        meta: {
            ...moment.meta,
        },
    }

    if (!moment) {
        return null
    }

    return (
        <>
            {!isLandscapeMode && (
                <Column
                    trkContext="Edit Moment Page"
                    className="EditMomentPage"
                >
                    <PersonalizeMoment
                        loadingMessage="Updating your highlight..."
                        fullGameMoment={fullGameMoment}
                        confirmButtonText="Update highlight"
                        onClose={handleOnClose}
                        onSave={handleOnSave}
                        moment={massagedMoment}
                        loading={loading}
                        allowEditSpotlight={false}
                        allowConfirmation={true}
                        allowEditTitle={false}
                        allowEditTime={false}
                        tagsType="game"
                        game={game}
                        open={true}
                    />
                </Column>
            )}
            {isLandscapeMode && (
                <Modal open={true}>
                    <TitleSection
                        className="header"
                        onCloseClick={handleOnClose}
                    >
                        <Label
                            type={Label.Type.Heading4}
                            content="Create moment"
                        />
                    </TitleSection>
                    <ErrorRotate />
                </Modal>
            )}
        </>
    )
}
